import { de } from "./de";
import { es } from "./es";
import { fr } from "./fr";
import { he } from "./he";
import { it } from "./it";
import { nl } from "./nl";
import { pl } from "./pl";
import { LOCALE_CODES, PAGE_KEYS, type LocaleCode, type LocalePack, type PageKey } from "./types";

export * from "./types";

export const LOCALES: Record<LocaleCode, LocalePack> = { de, it, he, es, fr, pl, nl };

export const SITE_URL = "https://carentals.ro";

export const isLocaleCode = (value: string): value is LocaleCode =>
  (LOCALE_CODES as readonly string[]).includes(value);

/** Path of a translated page, e.g. /de/mietwagen-otopeni */
export function localePath(code: LocaleCode, key: PageKey): string {
  const slug = LOCALES[code].pages[key].slug;
  return slug ? `/${code}/${slug}` : `/${code}`;
}

export function findPageBySlug(code: LocaleCode, slug: string): PageKey | undefined {
  return PAGE_KEYS.find((key) => LOCALES[code].pages[key].slug === slug);
}

/**
 * Existing English URLs. New locale pages are treated as translations of these;
 * pages without a dedicated English original point at the closest existing one.
 * These paths must never change — they are the live English site.
 */
export const EN_COUNTERPART: Record<PageKey, string> = {
  country: "/",
  "airport-otopeni": "/car-rental-otopeni-airport",
  "airport-cluj": "/rentals",
  "airport-iasi": "/rentals",
  "airport-timisoara": "/rentals",
  "airport-sibiu": "/rentals",
  "city-bucharest": "/",
  "city-brasov": "/rentals",
  "city-cluj": "/rentals",
  "city-constanta": "/rentals",
  cheap: "/car-rental-no-deposit-bucharest",
  automatic: "/rentals",
  "unlimited-km": "/rentals",
  "one-way": "/rentals",
  "seven-seater": "/rentals",
  "guide-transfagarasan": "/blog",
  "guide-transylvania": "/blog",
  "guide-black-sea": "/blog",
  "rules-rovinieta": "/good-to-know",
  "rules-winter-tires": "/good-to-know",
  "rules-speed-cameras": "/good-to-know",
  "airport-pickup": "/car-rental-otopeni-airport",
};

/** Existing Romanian URLs (unchanged live pages). */
export const RO_COUNTERPART: Partial<Record<PageKey, string>> = {
  country: "/ro",
  "airport-otopeni": "/ro/inchirieri-auto-otopeni",
  cheap: "/ro/inchirieri-auto-fara-garantie",
};

/**
 * Page keys whose English/Romanian counterparts are dedicated translations that
 * emit return tags back to the locale mirrors. Only these may be advertised as
 * en/ro alternates — otherwise crawlers report "no return tag".
 */
export const RECIPROCAL_KEYS: PageKey[] = ["country", "airport-otopeni", "cheap"];

/**
 * hreflang set for a page that exists in the new locales.
 * en/ro are only advertised when the English/Romanian page links back.
 */
export function localeHreflangLinks(key: PageKey) {
  const reciprocal = RECIPROCAL_KEYS.includes(key);
  const en = EN_COUNTERPART[key];
  const ro = RO_COUNTERPART[key];
  const locales = LOCALE_CODES.map((code) => ({
    rel: "alternate",
    hrefLang: code,
    href: `${SITE_URL}${localePath(code, key)}`,
  }));
  if (!reciprocal) {
    // No EN/RO mirror for this page, so x-default falls back to the first
    // locale in the group — every group must still declare one.
    return [
      ...locales,
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: `${SITE_URL}${localePath(LOCALE_CODES[0]!, key)}`,
      },
    ];
  }
  return [
    { rel: "alternate", hrefLang: "en", href: `${SITE_URL}${en}` },
    ...(ro ? [{ rel: "alternate", hrefLang: "ro", href: `${SITE_URL}${ro}` }] : []),
    ...locales,
    { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}${en}` },
  ];
}


/** Reverse lookup used to extend hreflang on the existing EN/RO pages. */
export function pageKeyForEnglishPath(pathname: string): PageKey | undefined {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return (Object.keys(EN_COUNTERPART) as PageKey[]).find(
    (key) => EN_COUNTERPART[key] === clean && ["country", "airport-otopeni", "cheap"].includes(key),
  );
}

/** All new-locale URLs, for the sitemap. */
export function allLocaleUrls(): { path: string; code: LocaleCode; key: PageKey }[] {
  return LOCALE_CODES.flatMap((code) =>
    PAGE_KEYS.map((key) => ({ path: localePath(code, key), code, key })),
  );
}

export const LOCALE_GROUPS: { label: keyof LocaleUiNav; keys: PageKey[] }[] = [
  { label: "airports", keys: PAGE_KEYS.filter((k) => k.startsWith("airport-")) },
  { label: "cities", keys: PAGE_KEYS.filter((k) => k.startsWith("city-")) },
  {
    label: "guides",
    keys: PAGE_KEYS.filter((k) => k.startsWith("guide-") || k.startsWith("rules-")),
  },
];

type LocaleUiNav = LocalePack["ui"]["nav"];
